import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Eye, EyeOff, ArrowLeft, Loader2, X, Check } from "lucide-react";
import axios from "axios";
import { API } from "../lib/helpers";
import { trackSignUp, trackLogin } from "../lib/tracking";

const LEGAL_CONTENT = {
  terms: {
    title: "Terms of Service",
    slug: "terms",
  },
  privacy: {
    title: "Privacy Policy",
    slug: "privacy",
  },
  cookies: {
    title: "Cookie Policy",
    slug: "cookies",
  },
  "investment-disclaimer": {
    title: "Investment Disclaimer",
    slug: "investment-disclaimer",
  },
};

const PASSWORD_RULES = [
  { key: "length", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { key: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { key: "special", label: "One special character (!@#$%^&*...)", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

const LegalModal = ({ slug, onClose }) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useState(() => {
    import("./LegalPage").then((mod) => {
      const pages = mod.LEGAL_PAGES;
      if (pages && pages[slug]) {
        setContent(pages[slug]);
      }
      setLoading(false);
    });
  }, [slug]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="legal-modal">
      <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
          <button onClick={onClose} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="legal-modal-back">
            <ArrowLeft className="w-4 h-4" /> Back to Sign Up
          </button>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="legal-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : content ? (
            <>
              <h2 className="text-xl font-bold mb-4" data-testid="legal-modal-title">{content.title}</h2>
              <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {content.content}
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Content not found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

const AuthPage = ({ login, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/dashboard";
  const initialMode = searchParams.get("mode");
  const [tab, setTab] = useState(initialMode === "signup" ? "register" : "login");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [legalSlug, setLegalSlug] = useState(null);

  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");

  // Register state
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);

  const passwordValid = useMemo(
    () => PASSWORD_RULES.every((r) => r.test(regPassword)),
    [regPassword]
  );
  const passwordsMatch = regPassword === regConfirmPassword && regConfirmPassword.length > 0;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: loginEmail.trim(),
        password: loginPassword,
      });
      // SEC-004: session comes from the httpOnly cookie set on this
      // response. No longer mirror the token into localStorage.
      if (res.data) {
        if (setUser) setUser(res.data);
        trackLogin();
        toast.success(`Welcome back, ${res.data.name || ""}!`);
        navigate(redirectTo);
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      toast.error(detail || `Login failed (${status || err.message})`);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      toast.error("Password doesn't meet requirements");
      return;
    }
    if (!passwordsMatch) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        name: regName,
        email: regEmail,
        password: regPassword,
        marketing_consent: marketingConsent,
      });
      // SEC-004: session comes from the httpOnly cookie set on this
      // response. No longer mirror the token into localStorage.
      if (res.data) {
        if (setUser) setUser(res.data);
        trackSignUp("email");
        toast.success(`Welcome, ${res.data.name || regName}! Account created.`);
        navigate(redirectTo);
      } else {
        toast.error("Unexpected response from server");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      toast.error(detail || `Registration failed (${status || err.message})`);
    } finally {
      setLoading(false);
    }
  };

  const openLegal = (e, slug) => {
    e.preventDefault();
    setLegalSlug(slug);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {legalSlug && <LegalModal slug={legalSlug} onClose={() => setLegalSlug(null)} />}

      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
          data-testid="auth-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </button>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight text-emerald-500">RealixData</h1>
          <p className="text-sm text-muted-foreground mt-1">Investment intelligence for real estate</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="login" data-testid="auth-login-tab">Log In</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-register-tab">Sign Up</TabsTrigger>
          </TabsList>

          {/* LOGIN */}
          <TabsContent value="login">
            <Card className="border-border bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Welcome back</CardTitle>
                <CardDescription>Log in to access your dashboard</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Enter your password"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        required
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Log In
                  </Button>
                  <button
                    type="button"
                    onClick={() => window.location.href = "/reset-password"}
                    className="text-xs text-muted-foreground hover:text-emerald-500 w-full text-center transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot your password?
                  </button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* REGISTER */}
          <TabsContent value="register">
            <Card className="border-border bg-card">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg">Create your account</CardTitle>
                <CardDescription>Start finding profitable properties</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="reg-name">Full Name</Label>
                    <Input
                      id="reg-name"
                      type="text"
                      placeholder="Your name"
                      value={regName}
                      onChange={(e) => setRegName(e.target.value)}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="you@example.com"
                      value={regEmail}
                      onChange={(e) => setRegEmail(e.target.value)}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="reg-password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password"
                        value={regPassword}
                        onChange={(e) => setRegPassword(e.target.value)}
                        required
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {/* Password requirements */}
                    {regPassword.length > 0 && (
                      <div className="mt-2 space-y-1" data-testid="password-requirements">
                        {PASSWORD_RULES.map((rule) => {
                          const passes = rule.test(regPassword);
                          return (
                            <div key={rule.key} className={`flex items-center gap-1.5 text-[11px] ${passes ? "text-emerald-500" : "text-muted-foreground"}`}>
                              {passes ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                              {rule.label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="reg-confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="reg-confirm-password"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Re-enter your password"
                        value={regConfirmPassword}
                        onChange={(e) => setRegConfirmPassword(e.target.value)}
                        required
                        data-testid="register-confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {regConfirmPassword.length > 0 && !passwordsMatch && (
                      <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1" data-testid="password-mismatch-error">
                        <X className="w-3 h-3" /> Passwords don't match
                      </p>
                    )}
                    {passwordsMatch && (
                      <p className="text-[11px] text-emerald-500 mt-1 flex items-center gap-1" data-testid="password-match-ok">
                        <Check className="w-3 h-3" /> Passwords match
                      </p>
                    )}
                  </div>
                  <label className="flex items-start gap-2.5 cursor-pointer" data-testid="marketing-consent-label">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-border accent-emerald-600"
                      data-testid="marketing-consent-checkbox"
                    />
                    <span className="text-[11px] text-muted-foreground leading-relaxed">
                      I agree to receive marketing emails, deal alerts, and promotional content from RealixData. You can unsubscribe at any time.
                    </span>
                  </label>
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={loading || !passwordValid || !passwordsMatch}
                    data-testid="register-submit-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Account
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center leading-relaxed mt-3">
                    By creating an account, you agree to the{" "}
                    <button type="button" onClick={(e) => openLegal(e, "terms")} className="text-emerald-500 hover:underline" data-testid="legal-link-terms">Terms of Service</button>,{" "}
                    <button type="button" onClick={(e) => openLegal(e, "privacy")} className="text-emerald-500 hover:underline" data-testid="legal-link-privacy">Privacy Policy</button>,{" "}
                    <button type="button" onClick={(e) => openLegal(e, "cookies")} className="text-emerald-500 hover:underline" data-testid="legal-link-cookies">Cookie Policy</button>, and{" "}
                    <button type="button" onClick={(e) => openLegal(e, "investment-disclaimer")} className="text-emerald-500 hover:underline" data-testid="legal-link-disclaimer">Investment Disclaimer</button>.
                  </p>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AuthPage;
